import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { Fragment } from "react";
import { Can } from "@/components/citadel/can";
import { client } from "@/hono";
import { BookLoadCarrierTransactionModal } from "@/modals/stock/book-load-carrier-transaction-modal";
import type { LoadCarrierPanelBaseProps } from "../shared";
import { columns } from "./table-config";

/**
 * A carrier's movement ledger, and the one button that adds to it.
 *
 * The button lives here rather than on the general panel because this is the table it changes: a
 * balance is not edited, it is explained, and the explanation is a row in this list.
 */
export function LoadCarrierTransactions({ loadCarrierId }: LoadCarrierPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const bookModal = useDisclosure();

	/** Query */
	const { data, isLoading, refetch, queryKey } = useQuery({
		queryKey: ["load-carrier-transactions", loadCarrierId],
		queryFn: async () => {
			const response = await client.stock["load-carriers"][":loadCarrierId"].transactions.$get({
				param: { loadCarrierId },
			});
			const data = await responseHandler(response);
			return guardArray(data.transactions);
		},
	});

	/** Render */
	return (
		<Fragment>
			<DataTable
				data={guardArray(data)}
				columns={columns}
				refetch={refetch}
				isLoading={isLoading}
				queryKey={queryKey}
				persistenceKey="load-carrier-transactions-table"
				menu={
					<Can
						action="update"
						subject="Stock"
					>
						<Button
							onClick={bookModal.onOpen}
							variant="primary"
							icon={<Icon name="Plus" />}
						>
							{t("stock.book_load_carrier_movement")}
						</Button>
					</Can>
				}
			/>
			{bookModal.isOpen && (
				<BookLoadCarrierTransactionModal
					isOpen={bookModal.isOpen}
					onClose={bookModal.onClose}
					onComplete={refetch}
					loadCarrierId={loadCarrierId}
				/>
			)}
		</Fragment>
	);
}
